<!--
  DistributorOrdersTable -- presentational cross-store orders table.

  Card: Pricing & Splits -- Reporting & Insights (C)
  (https://trello.com/c/pSDstQD3).

  Props-driven: renders the LOCKED distributor-orders wire rows
  (GET /api/reporting/distributor-orders) into a Date / Order # / Store /
  Shopper / Status / Total table. Each row is clickable and navigates to the
  per-order detail route. No fetching, no scope logic -- the parent
  (DistributorOrdersList) owns that. Money arrives as `total_dollars` (a
  number, never null) and is formatted via the shared currency helper so it
  matches every other currency surface in the app.

  order_number CAN be null on the wire; the number cell falls back to the
  order id so the row still identifies itself.
-->
<template>
  <div class="dord-table-wrap">
    <v-table
      density="comfortable"
      data-testid="dist-orders-table"
      class="dord-table"
    >
      <thead>
        <tr>
          <th class="text-left">Date</th>
          <th class="text-left">Order #</th>
          <th class="text-left">Store</th>
          <th class="text-left">Shopper</th>
          <th class="text-left">Status</th>
          <th class="text-right">Total</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="order in orders"
          :key="order.id"
          class="dord-row"
          :data-testid="`dist-orders-row-${order.id}`"
          role="button"
          tabindex="0"
          @click="openOrder(order)"
          @keydown.enter="openOrder(order)"
        >
          <td data-testid="dist-orders-row-date">
            {{ formatDate(order.placed_at) }}
          </td>
          <td data-testid="dist-orders-row-number">
            {{ orderNumber(order) }}
          </td>
          <td data-testid="dist-orders-row-store">
            {{ order.store_name || "--" }}
          </td>
          <td data-testid="dist-orders-row-shopper">
            {{ order.shopper_name || "--" }}
          </td>
          <td data-testid="dist-orders-row-status">
            <div class="dord-status">
              <!-- Placed into a batch that has not released yet: the card is
                   charged at release, so this order is NOT revenue yet. -->
              <span
                v-if="isBatchPending(order)"
                class="dord-pending__label"
                data-testid="dist-orders-row-batch-pending"
              >
                Awaiting batch release
                <span class="dord-pending__note">Not yet charged</span>
              </span>
              <template v-else>
                {{ statusLabel(order.status) }}
              </template>
              <!-- Any batched order says which batch it is in, labelled and
                   chipped exactly as the Batches page shows it. $0 / payroll
                   / Net orders join a batch but stay `processing`. -->
              <span
                v-if="batchView(order)"
                class="dord-pending__batch"
              >
                <span
                  class="dord-pending__batch-name"
                  data-testid="dist-orders-row-batch-name"
                >
                  {{ batchView(order).label }}
                </span>
                <BatchStatusChip
                  v-if="batchView(order).status"
                  :status="batchView(order).status"
                  testid="dist-orders-row-batch-chip"
                />
              </span>
            </div>
          </td>
          <td class="text-right" data-testid="dist-orders-row-total">
            {{ formatCurrency(order.total_dollars) }}
          </td>
        </tr>
      </tbody>
    </v-table>

    <!-- Empty state: only when we have zero rows AND are not mid-load. The
         parent (DistributorOrdersList) owns the primary "dist-orders-empty"
         message and never mounts the table when empty, so in practice this is
         only reachable when the table is used in isolation. Distinct testid to
         avoid a duplicate-selector collision for ui-runner / E2E. -->
    <p
      v-if="!loading && orders.length === 0"
      class="dord-empty"
      data-testid="dist-orders-table-empty"
      role="status"
    >
      No orders yet. Orders placed in your stores will appear here.
    </p>
  </div>
</template>

<script>
import { formatCurrency } from "@/utils/currency";
import BatchStatusChip from "@/pages/distributor/store-editor/StoreBatches/BatchStatusChip.vue";
import { normalizeBatch } from "@/services/batchManagementService";

// orders.status for an order placed into a batch that has not released (and
// so has not been charged) yet. Mirrors AWAITING_BATCH_CHARGE_STATUS in
// api/src/models/order.py.
const AWAITING_BATCH_CHARGE = "awaiting_batch_charge";

// Order status vocabulary -> human label. Superset of OrderDetailView's
// STATUS_LABELS and the portal serializer, PLUS the raw model states that the
// distributor-orders list passes through unmapped: the Order.status DB default
// is "processing" (api/src/models/order.py), so an order in its default state
// must render a friendly label rather than the raw string. Unknown statuses
// fall back to the raw value.
const STATUS_LABELS = {
  processing: "Processing",
  pending: "Placed",
  in_production: "In production",
  shipped: "Shipped",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
  // The DECORATOR cancelled (back office). Owner-only; the label is Jake's,
  // verbatim. Buyers are served `processing` instead.
  decorator_cancelled: "Decorator cancelled",
  [AWAITING_BATCH_CHARGE]: "Awaiting batch release",
};

export default {
  name: "DistributorOrdersTable",
  components: { BatchStatusChip },
  props: {
    // Rows in the LOCKED wire shape (see DistributorOrdersList / service).
    orders: {
      type: Array,
      default: () => [],
    },
    // True while the parent is fetching; suppresses the empty state so we do
    // not flash "No orders yet" during the initial load.
    loading: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    formatCurrency,
    isBatchPending(order) {
      return order.status === AWAITING_BATCH_CHARGE;
    },
    // The row's batch as the Batches page and the order detail page show it:
    // ONE label mechanism (normalizeBatch -> "B-<id>" for an unnamed batch).
    // No chip for a legacy batch with no lifecycle state: normalizeBatch
    // would default it to "open", which would be a false claim.
    batchView(order) {
      const raw = order.batch;
      if (!raw || typeof raw !== "object") return null;
      const normalized = normalizeBatch(raw);
      return {
        label: normalized.id,
        status: raw.state ? normalized.status : null,
      };
    },
    orderNumber(order) {
      // order_number CAN be null on the wire; fall back to the id.
      if (order.order_number) return order.order_number;
      return `#${order.id}`;
    },
    statusLabel(status) {
      if (!status) return "--";
      return STATUS_LABELS[status] || status;
    },
    formatDate(iso) {
      if (!iso) return "--";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "--";
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    openOrder(order) {
      // Navigate to the per-order detail route. The reportRoutes group is
      // mounted under the /distributor prefix, so the absolute path is
      // /distributor/reporting/orders/:orderId.
      this.$router.push(`/distributor/reporting/orders/${order.id}`);
    },
  },
};
</script>

<style scoped>
.dord-table-wrap {
  width: 100%;
}
.dord-row {
  cursor: pointer;
}
.dord-row:hover {
  background: var(--tf-surface-hover, rgba(0, 0, 0, 0.03));
}
.dord-status {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.dord-pending__label {
  font-weight: 500;
}
.dord-pending__note {
  display: block;
  font-weight: 400;
  font-size: 12px;
  /* Neutral, not amber: the batch chip beside it uses amber for Holding
     only, and one colour must not carry two meanings in one cell. */
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
}
.dord-pending__batch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
}
.dord-pending__batch-name {
  white-space: nowrap;
}
.dord-empty {
  padding: var(--tf-sp-6, 24px) var(--tf-sp-4, 16px);
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
  font-style: italic;
  text-align: center;
}
</style>
